// AeroSettingsSheet — deep-settings bottom sheet for aeronautical layers.
//
// Opened by the "···" button in the AeroKnobCluster quick-pop strip.
// Shows all aero-layer toggles in one place, organized into two sections:
//   AIRPORTS  — airports, heliports, fuel (100LL / Jet-A)
//   AIRSPACE  — class B/C/D, SUA, waypoints, fixes, airways
//
// Tapping the backdrop dismisses the sheet.

import { useUiStore } from '../stores/uiStore';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import type { LayerId } from './layerCatalog';

interface ToggleRowProps {
  id: LayerId;
  label: string;
  sub?: string;
}

// Hospital-only sub-filter under the Heliports toggle. Renders
// indented + disabled when Heliports is OFF so the relationship is
// visually obvious. Wiley HEMS-ops req 2026-05-13: "toggle that brings
// up only hospital helipads." Detected by facility-name match
// server-side; see api/bbox.ts isHospitalHeliport for the heuristic.
function HospitalHeliportsRow() {
  const heliportsOn = useLayerVisibilityStore((s) => s.layers.heliports);
  const on          = useUiStore((s) => s.heliportsHospitalsOnly);
  const setOn       = useUiStore((s) => s.setHeliportsHospitalsOnly);
  const dim = !heliportsOn;
  return (
    <div className={`flex items-center justify-between py-2 pl-5 ${dim ? 'opacity-50' : ''}`}>
      <div className="min-w-0">
        <div className="text-sm text-cockpit-100 leading-tight">Hospitals only</div>
        <div className="text-[10px] text-cockpit-400 leading-tight mt-0.5">
          Filter heliports to hospital / medical-center pads
        </div>
      </div>
      <button
        onClick={() => heliportsOn && setOn(!on)}
        disabled={!heliportsOn}
        aria-pressed={on}
        className={[
          'relative w-10 h-6 rounded-full transition-colors flex-shrink-0',
          on ? 'bg-ember-600' : 'bg-cockpit-600',
        ].join(' ')}
        aria-label="Toggle hospital heliports only"
      >
        <span
          className={[
            'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
            on ? 'translate-x-4' : 'translate-x-0',
          ].join(' ')}
        />
      </button>
    </div>
  );
}

/**
 * Enable 3D toggle — day-1 spike 2026-05-16. Renders the existing
 * classBCD / TFR / SUA polygons as fill-extrusion volumes when the map
 * is pitched. Independent of the per-layer toggles above so users can
 * turn 3D on without forcing all three sources visible.
 */
function Enable3dRow() {
  const on = useUiStore((s) => s.enable3d);
  const setOn = useUiStore((s) => s.setEnable3d);
  return (
    <div className="flex items-center justify-between py-2">
      <div className="min-w-0">
        <div className="text-sm text-cockpit-100 leading-tight">3D airspace (beta)</div>
        <div className="text-[10px] text-cockpit-400 leading-tight mt-0.5">
          Wedding-cake Class B · TFR cylinders · visible when pitched
        </div>
      </div>
      <button
        onClick={() => setOn(!on)}
        aria-pressed={on}
        className={[
          'relative w-10 h-6 rounded-full transition-colors flex-shrink-0',
          on ? 'bg-ember-600' : 'bg-cockpit-600',
        ].join(' ')}
        aria-label="Toggle 3D airspace volumes"
      >
        <span
          className={[
            'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
            on ? 'translate-x-4' : 'translate-x-0',
          ].join(' ')}
        />
      </button>
    </div>
  );
}

function ToggleRow({ id, label, sub }: ToggleRowProps) {
  const on         = useLayerVisibilityStore((s) => s.layers[id]);
  const toggleLayer = useLayerVisibilityStore((s) => s.toggleLayer);

  return (
    <div className="flex items-center justify-between py-2">
      <div className="min-w-0">
        <div className="text-sm text-cockpit-100 leading-tight">{label}</div>
        {sub && <div className="text-[10px] text-cockpit-400 leading-tight mt-0.5">{sub}</div>}
      </div>
      <button
        onClick={() => toggleLayer(id)}
        aria-pressed={on}
        className={[
          'relative w-10 h-6 rounded-full transition-colors flex-shrink-0',
          on ? 'bg-ember-600' : 'bg-cockpit-600',
        ].join(' ')}
        aria-label={`Toggle ${label}`}
      >
        <span
          className={[
            'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
            on ? 'translate-x-4' : 'translate-x-0',
          ].join(' ')}
        />
      </button>
    </div>
  );
}

interface Props {
  onClose: () => void;
}

export default function AeroSettingsSheet({ onClose }: Props) {
  return (
    // Full-screen backdrop. On mobile/tablet the sheet anchors to the
    // bottom edge full-width; on desktop we constrain its width so the
    // label/toggle pairs don't stretch across a 1500-2400px iMac viewport
    // with the toggle floating far away from its label.
    //
    // Boss 2026-05-16: "on desktop the label is all the way on the left
    // side of the screen, the toggle button is all the way on the right
    // side of the screen. Hard to align the two."
    //
    // md: breakpoint = 768px. Below it we keep bottom-anchored full-width
    // (the right pattern for touch). At or above 768px we cap at a
    // readable column width and center the sheet horizontally.
    <div
      className="absolute inset-0 z-40 flex items-end justify-center md:items-end md:justify-center"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full md:w-[28rem] md:max-w-md bg-cockpit-800/98 backdrop-blur border-t border-cockpit-600 rounded-t-2xl md:rounded-2xl md:mb-4 md:border shadow-rail p-4 pb-8 max-h-[75vh] overflow-y-auto scroll-touch"
        style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Aero layer settings"
      >
        {/* Handle bar */}
        <div className="flex justify-center mb-3">
          <div className="w-10 h-1 bg-cockpit-600 rounded-full" />
        </div>

        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-cockpit-100 text-base">Aero Layers</h3>
          <button
            onClick={onClose}
            className="text-cockpit-300 hover:text-cockpit-100 text-sm"
            aria-label="Close"
          >✕</button>
        </div>

        {/* ── Airports section ── */}
        <div className="text-[10px] tracking-widest text-cockpit-300 mb-1">AIRPORTS</div>
        <div className="divide-y divide-cockpit-700/50 mb-4">
          <ToggleRow id="airports"   label="Airports"     sub="FAA NASR · ICAO symbology" />
          <ToggleRow id="heliports"  label="Heliports"    sub="NASR · z≥10 · opt-in for HEMS / fire" />
          <HospitalHeliportsRow />
          <ToggleRow id="fuel100ll"  label="Fuel: 100LL"  sub="NASR · avgas-equipped fields" />
          <ToggleRow id="fueljeta"   label="Fuel: Jet-A"  sub="NASR · turbine-fuel fields" />
        </div>

        {/* ── Airspace section ── */}
        <div className="text-[10px] tracking-widest text-cockpit-300 mb-1">AIRSPACE</div>
        <div className="divide-y divide-cockpit-700/50 mb-4">
          <ToggleRow id="classBCD"   label="Class B / C / D"  sub="FAA controlled airspace · lazy" />
          <ToggleRow id="sua"        label="Special Use Airspace" sub="MOA / R / W / P · lazy" />
          <Enable3dRow />
        </div>

        {/* ── En-route section ── */}
        <div className="text-[10px] tracking-widest text-cockpit-300 mb-1">EN-ROUTE</div>
        <div className="divide-y divide-cockpit-700/50">
          <ToggleRow id="waypoints"  label="Navaids"    sub="VOR / VOR-DME / VORTAC / NDB" />
          <ToggleRow id="fixes"      label="Fixes"      sub="5-letter named waypoints · z≥8" />
          <ToggleRow id="airways"    label="Airways"    sub="FAA ATS · Victor / Jet / RNAV" />
        </div>

        <p className="mt-4 text-[10px] leading-snug text-cockpit-300">
          Supplemental only — verify with current FAA sources before flight.
        </p>
      </div>
    </div>
  );
}
